import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import dayjs from "dayjs";
import "./styles.css";
import {
  getZonedDate,
  useGetTimezone,
} from "../../components/ZonedDateTimePicker";
import { LoadingOverlay } from "../../components/Loading";

// Types
export interface BandBooking {
  id: string;
  bandId: string;
  stageId: string;
  startTime: Date;
  endTime: Date;
  band: {
    id: string;
    name: string;
    imageUrl?: string;
  };
  stage: {
    id: string;
    name: string;
    order: number;
  };
}

export interface ClashfinderData {
  name: string;
  locations: ClashfinderLocation[];
}

export interface ClashfinderLocation {
  name: string;
  events: ClashfinderEvent[];
  order: number;
}

export interface ClashfinderEvent {
  name: string;
  start: Date;
  end: Date;
  id: string;
  bookingId: string;
}

export interface ClashfinderBand {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  start: Date;
  end: Date;
  stage: string;
  id: string;
  bookingId: string;
  effectiveDate: number;
}

export interface ClashfinderStage {
  pos: number;
  name: string;
}

export interface ClashfinderDivider {
  pos: number;
  time: number;
}

export interface ClashfinderBandTime {
  pos: number;
  time: dayjs.Dayjs;
}

export interface ClashfinderConfig {
  startOffset: number;
  locationWidth: number;
  startHour: number;
  dayHeight: number;
  dividersCount: number;
  timeDisplayOffset: number;
  hourHeight: number;
  entireWidth: number;
}

// Helper function to create config based on number of locations and time range
const createConfig = (
  locationCount: number,
  timeRange?: { startHour: number; endHour: number }
): ClashfinderConfig => {
  const defaultStart = 10;
  const defaultEnd = 28; // 4 AM next day (24 + 4)

  const startHour = timeRange?.startHour ?? defaultStart;
  const endHour = timeRange?.endHour ?? defaultEnd;
  const totalHours = endHour - startHour;

  return {
    locationWidth: 200,
    dayHeight: totalHours * 120 + 200, // Dynamic height based on time range + padding
    hourHeight: 120,
    entireWidth: Math.max(1400, locationCount * 200 + 200),
    startOffset: 60,
    startHour,
    dividersCount: totalHours,
    timeDisplayOffset: 80,
  };
};

// Color constants
const COLORS = {
  backgroundColor: "#1a1a1a",
  primaryColorDark: "#2a2a2a",
  primaryColorDarker: "#0f0f0f",
  primaryColorText: "#ffffff",
  alternativeTextColor: "#000000",
  stageHeader: "#333333",
  bandColor: "#4a90e2",
  selectedBandColor: "#0084B5",
};

interface ClashfinderProps {
  bandBookings: BandBooking[];
  selectedDate?: Date;
  onBandClick?: (band: ClashfinderBand) => void;
  onDateChange?: (date: Date) => void;
}

export const Clashfinder: React.FC<ClashfinderProps> = ({
  bandBookings,
  onBandClick,
  onDateChange,
}) => {
  const [internalSelectedDate, setInternalSelectedDate] = useState<
    Date | undefined
  >();
  const [selectedBands, setSelectedBands] = useState<Set<string>>(new Set());
  const [nowLineYPos, setNowLineYPos] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { timezone, loading, error } = useGetTimezone();

  // Convert BandBooking[] to ClashfinderData
  const clashfinderData = useMemo(
    () => createClashfinderFromBookings(bandBookings, timezone),
    [bandBookings, timezone]
  );

  // Get available dates from bookings
  const availableDates = useMemo(() => {
    const dateSet = new Set<string>();
    bandBookings.forEach((booking) => {
      const startTime = dayjs(booking.startTime);
      // If event starts before 5 AM, consider it part of previous day
      let effectiveDate = startTime;
      if (startTime.hour() < 5) {
        effectiveDate = startTime.subtract(1, "day");
      }
      dateSet.add(effectiveDate.format("YYYY-MM-DD"));
    });
    return Array.from(dateSet).sort();
  }, [bandBookings]);

  // Initialize selected date to first available date
  const currentSelectedDate = useMemo(() => {
    if (internalSelectedDate) {
      return internalSelectedDate;
    }
    if (availableDates.length > 0) {
      const firstDate = new Date(availableDates[0]);
      setInternalSelectedDate(firstDate);
      return firstDate;
    }
    return new Date();
  }, [internalSelectedDate, availableDates]);

  // Calculate time range from band bookings
  const timeRange = useMemo(() => {
    if (bandBookings.length === 0) return undefined;

    let earliestHour = 24;
    let latestHour = 0;

    bandBookings.forEach((booking) => {
      const startHour = dayjs(booking.startTime).hour();
      const endHour = dayjs(booking.endTime).hour();
      const endMinute = dayjs(booking.endTime).minute();

      // Adjust for early morning hours (count as next day)
      const adjustedStartHour = startHour < 6 ? startHour + 24 : startHour;
      const adjustedEndHour = endHour < 6 ? endHour + 24 : endHour;

      earliestHour = Math.min(earliestHour, adjustedStartHour);
      latestHour = Math.max(
        latestHour,
        endMinute > 0 ? adjustedEndHour + 1 : adjustedEndHour
      );
    });

    // Add some padding
    earliestHour = Math.max(0, earliestHour - 1);
    latestHour = Math.min(30, latestHour + 1);

    return { startHour: earliestHour, endHour: latestHour };
  }, [bandBookings]);

  // Calculate positions and layout
  const { bands, stages, dividers, bandTimes, config } = useMemo(() => {
    const dynamicConfig = createConfig(
      clashfinderData.locations.length,
      timeRange
    );
    return calculateClashfinderLayout(
      clashfinderData,
      dynamicConfig,
      currentSelectedDate
    );
  }, [clashfinderData, timeRange, currentSelectedDate]);

  // Filter bands by selected date
  const filteredBands = useMemo(() => {
    return bands.filter((band) => {
      const bandDate = dayjs(band.start);
      const selected = dayjs(currentSelectedDate);

      // If band starts before 5 AM, consider it part of previous day
      let effectiveBandDate = bandDate;
      if (bandDate.hour() < 5) {
        effectiveBandDate = bandDate.subtract(1, "day");
      }

      return effectiveBandDate.isSame(selected, "date");
    });
  }, [bands, currentSelectedDate]);

  // Get stages that have bands on the selected date
  const activeStages = useMemo(() => {
    const stagesWithBands = new Set(filteredBands.map((band) => band.stage));
    return stages.filter((stage) => stagesWithBands.has(stage?.name));
  }, [stages, filteredBands]);

  // Update now line position
  const updateNowLine = useCallback(() => {
    const now = dayjs();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    if (dayjs(currentSelectedDate).isSame(now, "date")) {
      const minHeight = config.hourHeight / 60;
      let y =
        (currentHour - config.startHour) * config.hourHeight +
        config.startOffset;
      y += minHeight * currentMinute;
      setNowLineYPos(y);
    }
  }, [currentSelectedDate, config]);

  // Update now line every 5 minutes
  useEffect(() => {
    updateNowLine();
    const interval = setInterval(updateNowLine, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [updateNowLine]);

  const handleDateChange = useCallback(
    (dateString: string) => {
      const newDate = new Date(dateString);
      setInternalSelectedDate(newDate);
      onDateChange?.(newDate);
    },
    [onDateChange]
  );

  const handleBandClick = useCallback(
    (band: ClashfinderBand) => {
      setSelectedBands((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(band.bookingId)) {
          newSet.delete(band.bookingId);
        } else {
          newSet.add(band.bookingId);
        }
        return newSet;
      });
      onBandClick?.(band);
    },
    [onBandClick]
  );

  // Calculate dynamic dimensions based on content
  const dynamicDimensions = useMemo(() => {
    // Width calculation
    let width = window.innerWidth;
    if (activeStages.length > 0) {
      const lastStagePos = Math.max(...activeStages.map((stage) => stage.pos));
      const minNeededWidth = lastStagePos + config.locationWidth + 100;
      width = Math.max(minNeededWidth, 800);
    }

    // Height calculation - use actual content height or minimum viewport
    let height = config.dayHeight;
    if (filteredBands.length === 0) {
      height = Math.min(600, window.innerHeight - 200); // Minimum height when no bands
    }

    return { width, height };
  }, [
    activeStages,
    config.locationWidth,
    config.dayHeight,
    filteredBands.length,
  ]);

  const entireWidthOrWindowWidth = dynamicDimensions.width;

  return (
    <div
      className="clashfinder"
      style={{ backgroundColor: COLORS.backgroundColor }}
    >
      <div className="clashfinder-container">
        {/* Date Selector */}
        <div className="date-selector">
          <label htmlFor="date-select" className="date-label">
            Select Date:
          </label>
          <select
            id="date-select"
            value={
              currentSelectedDate
                ? dayjs(currentSelectedDate).format("YYYY-MM-DD")
                : availableDates[0] || ""
            }
            onChange={(e) => handleDateChange(e.target.value)}
            className="date-dropdown"
          >
            {availableDates.map((date) => (
              <option key={date} value={date}>
                {dayjs(date).format("dddd, MMMM D, YYYY")}
              </option>
            ))}
          </select>
        </div>

        <div className="clashfinder-content" ref={scrollRef}>
          {/* Stage Header */}
          <div
            className="stage-header"
            style={{
              width: entireWidthOrWindowWidth,
              backgroundColor: COLORS.stageHeader,
            }}
          >
            {activeStages.map((stage) => (
              <div
                key={`stage-${stage?.name}`}
                className="stage-name"
                style={{
                  left: stage.pos,
                  width: config.locationWidth,
                  color: COLORS.primaryColorText,
                }}
              >
                {stage?.name}
              </div>
            ))}
          </div>

          {/* Main Grid */}
          <div
            className="clashfinder-grid"
            style={{
              width: entireWidthOrWindowWidth,
              height: dynamicDimensions.height + 100,
              backgroundColor: COLORS.backgroundColor,
            }}
          >
            {/* Horizontal Dividers */}
            {dividers.map((divider) => (
              <div
                key={`divider-${divider.time}`}
                className="divider horizontal"
                style={{
                  top: divider.pos,
                  width: entireWidthOrWindowWidth,
                  borderColor: COLORS.primaryColorText,
                }}
              />
            ))}

            {/* Vertical Lines */}
            {activeStages.map((stage) => (
              <div
                key={`vertical-${stage?.name}`}
                className="divider vertical"
                style={{
                  left: stage.pos,
                  height: "100%",
                  borderColor: COLORS.primaryColorText,
                }}
              />
            ))}

            {/* Now Line */}
            {dayjs(currentSelectedDate).isSame(dayjs(), "date") && (
              <div
                className="now-line"
                style={{
                  top: nowLineYPos,
                  width: entireWidthOrWindowWidth,
                  borderColor: COLORS.primaryColorText,
                }}
              />
            )}

            {/* Time Labels */}
            {bandTimes.map((timeItem) => (
              <div
                key={`time-${timeItem.time.hour()}`}
                className="time-label"
                style={{
                  top: timeItem.pos - 20,
                  left: 20,
                  color: COLORS.primaryColorText,
                }}
              >
                {timeItem.time.format("HH:mm")}
              </div>
            ))}

            {/* Bands */}
            {filteredBands.map((band) => (
              <BandComponent
                key={`${band.stage}-${band.name}-${band.start}`}
                band={band}
                config={config}
                isSelected={selectedBands.has(band.bookingId)}
                onClick={() => handleBandClick(band)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Band Component
interface BandComponentProps {
  band: ClashfinderBand;
  config: ClashfinderConfig;
  isSelected: boolean;
  onClick: () => void;
}

const BandComponent: React.FC<BandComponentProps> = ({
  band,
  config,
  isSelected,
  onClick,
}) => {
  const duration = dayjs(band.end).diff(dayjs(band.start), "minute");
  const shortDuration = duration < 30;
  const fontScale = duration < 30 ? duration / 25 : 1;

  const actualBandWidth = config.locationWidth * 0.9;
  const margin = (config.locationWidth - actualBandWidth) / 2;

  const timeText = `${dayjs(band.start).format("HH:mm")} - ${dayjs(
    band.end
  ).format("HH:mm")}`;

  return (
    <div
      className={`band ${isSelected ? "selected" : ""}`}
      style={{
        top: band.y,
        left: band.x + margin,
        width: actualBandWidth,
        height: band.height,
        backgroundColor: isSelected
          ? COLORS.selectedBandColor
          : COLORS.bandColor,
        padding: shortDuration ? 2 : 4,
      }}
      onClick={onClick}
    >
      <div className="band-name" style={{ fontSize: 16 * fontScale }}>
        {band.name}
      </div>
      <div className="band-time" style={{ fontSize: 12 * fontScale }}>
        {timeText}
      </div>
    </div>
  );
};

// Helper function to create clashfinder data from bookings
export const createClashfinderFromBookings = (
  bookings: BandBooking[],
  timezone: string
): ClashfinderData => {
  const stageMap = new Map<string, ClashfinderLocation>();

  bookings.forEach((booking) => {
    const stageId = booking.stage?.name;

    if (!stageMap.has(stageId)) {
      stageMap.set(stageId, {
        name: booking.stage?.name,
        order: booking.stage?.order,
        events: [],
      });
    }

    const stage = stageMap.get(stageId)!;
    stage.events.push({
      name: booking.band.name,
      start: getZonedDate(
        new Date(booking.startTime).toISOString(),
        false,
        timezone
      ) as Date,
      end: getZonedDate(
        new Date(booking.endTime).toISOString(),
        false,
        timezone
      ) as Date,
      id: booking.bandId,
      bookingId: booking.id,
    });
  });

  const result = {
    name: "Festival",
    locations: Array.from(stageMap.values()).sort((a, b) => a.order - b.order),
  };

  return result;
};

// Helper function to calculate layout
const calculateClashfinderLayout = (
  data: ClashfinderData,
  config: ClashfinderConfig,
  selectedDate: Date
) => {
  const selectedDay = dayjs(selectedDate);

  const filteredLocations = data.locations
    .filter((location) =>
      location.events.some((event) => {
        const eventStart = dayjs(event.start);
        const effectiveStart =
          eventStart.hour() < 5 ? eventStart.subtract(1, "day") : eventStart;
        return effectiveStart.isSame(selectedDay, "date");
      })
    )
    .sort((a, b) => a.order - b.order);

  const stages: ClashfinderStage[] = filteredLocations.map(
    (location, index) => ({
      pos: index * config.locationWidth + config.timeDisplayOffset,
      name: location.name,
    })
  );

  const dividers: ClashfinderDivider[] = [];
  for (let i = 0; i < config.dividersCount; i++) {
    dividers.push({
      pos: config.startOffset + i * config.hourHeight,
      time: config.startHour + i,
    });
  }

  const bandTimes: ClashfinderBandTime[] = [];
  for (let i = 0; i < config.dividersCount; i++) {
    bandTimes.push({
      pos: config.startOffset + i * config.hourHeight,
      time: dayjs()
        .hour(config.startHour + i)
        .minute(0),
    });
  }

  const bands: ClashfinderBand[] = [];
  filteredLocations.forEach((location, locationIndex) => {
    location.events.forEach((event) => {
      const x = locationIndex * config.locationWidth + config.timeDisplayOffset;

      const bandStartHour = dayjs(event.start).hour();
      const bandStartMin = dayjs(event.start).minute();

      const minHeight = config.hourHeight / 60;
      let y =
        (bandStartHour - config.startHour) * config.hourHeight +
        config.startOffset;
      y += minHeight * bandStartMin;

      const duration = dayjs(event.end).diff(dayjs(event.start), "minutes");
      const height = duration * minHeight;

      const start = dayjs(event.start);
      let effectiveDate = start.date();

      if (start.hour() < 5) {
        y = y + 24 * config.hourHeight;
        effectiveDate = effectiveDate - 1;
      }

      bands.push({
        x,
        y,
        width: config.locationWidth * 0.9,
        height,
        name: event.name,
        start: event.start,
        end: event.end,
        stage: location.name,
        id: event.id,
        bookingId: event.bookingId,
        effectiveDate,
      });
    });
  });

  return {
    bands,
    stages,
    dividers,
    bandTimes,
    config,
  };
};

export default Clashfinder;
